import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import { SITE } from "@/lib/site";

const CONTACT_ENDPOINT = "/contact.php";

const initialForm = { name: "", email: "", phone: "", message: "", website: "" };

const Contacto = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const isValid =
    form.name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.message.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "loading") return;
    if (!isValid) {
      setStatus({ state: "error", message: "Por favor completa los campos obligatorios." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(CONTACT_ENDPOINT, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        website: form.website,
      });
      setStatus({
        state: "success",
        message: "¡Mensaje enviado! Te responderemos lo antes posible.",
      });
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos por WhatsApp.";
      setStatus({ state: "error", message: detail });
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-12 h-px bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Contáctanos</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
              Hablemos de tu <span className="text-[#D4AF37]">próximo sitio web</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed">
              Cuéntanos sobre tu negocio y tus objetivos. Te responderemos con
              una propuesta clara y personalizada.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Info side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
                Atención directa
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-4">
                ¿Prefieres una respuesta inmediata?
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                Escríbenos por WhatsApp y atenderemos tu duda al instante. Estamos disponibles para ayudarte a llevar tu negocio al siguiente nivel.
              </p>
            </div>

            <div className="space-y-4">
              <a
                data-testid="contact-info-phone"
                href={`tel:+${SITE.whatsappNumber}`}
                className="flex items-center gap-4 border border-white/10 hover:border-[#D4AF37]/50 p-5 transition-colors"
              >
                <Phone className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <div className="text-xs tracking-widest uppercase text-zinc-500">Teléfono</div>
                  <div className="text-white">{SITE.whatsappDisplay}</div>
                </div>
              </a>
              <a
                data-testid="contact-info-whatsapp"
                href={`https://wa.me/${SITE.whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 border border-white/10 hover:border-[#D4AF37]/50 p-5 transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <div className="text-xs tracking-widest uppercase text-zinc-500">WhatsApp</div>
                  <div className="text-white">Hablar ahora</div>
                </div>
              </a>
              <div className="flex items-center gap-4 border border-white/10 p-5">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <div className="text-xs tracking-widest uppercase text-zinc-500">Correo</div>
                  <div className="text-white">Usa el formulario →</div>
                </div>
              </div>
            </div>

            <WhatsAppButton
              testId="contact-wa-cta"
              label="Chatear por WhatsApp"
              variant="primary"
            />
          </motion.div>

          {/* Form side */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 border border-white/10 bg-[#0d0d0d] p-8 md:p-10 space-y-5"
            noValidate
          >
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
              <label htmlFor="website">Sitio web</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="name" className="block text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                Nombre completo *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                data-testid="contact-input-name"
                required
                className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none transition-colors"
                placeholder="Tu nombre"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                  Correo electrónico *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-input-email"
                  required
                  className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none transition-colors"
                  placeholder="tucorreo@ejemplo.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                  Teléfono (opcional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none transition-colors"
                  placeholder="+52 ..."
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                Mensaje *
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                required
                className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none transition-colors resize-y"
                placeholder="Cuéntanos sobre tu negocio y lo que necesitas..."
              />
            </div>

            {status.state === "success" && (
              <div
                data-testid="contact-success"
                className="flex items-start gap-3 border border-[#D4AF37]/60 bg-[#D4AF37]/5 p-4 text-[#D4AF37]"
              >
                <CheckCircle2 className="w-5 h-5 mt-0.5" />
                <p className="text-sm">{status.message}</p>
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid="contact-error"
                className="flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4 text-red-300"
              >
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <p className="text-sm">{status.message}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status.state === "loading"}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-semibold hover:bg-[#C5A059] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {status.state === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" /> Enviar mensaje
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-500 self-center">
                * Campos obligatorios
              </p>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
